import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { sharedStyles, buttonStyles, inputStyles, layoutStyles, typographyStyles } from '../styles/shared.js';
import { cartState, type Cart, type CartItem, formatPrice, getProductImageDisplay, resolveProductImageUrl } from '@landi/store-core';

/**
 * Full cart page component with line items and order summary
 * Styled to match The Golden Peptides design
 * 
 * @element landi-cart
 * @fires checkout-click - Fired when checkout button is clicked
 * @fires continue-shopping - Fired when continue shopping is clicked
 */
@customElement('landi-cart')
export class LandiCart extends LitElement {
  static styles = [
    sharedStyles,
    buttonStyles,
    inputStyles,
    layoutStyles,
    typographyStyles,
    css`
      :host {
        display: block;
        min-height: 100vh;
        background: var(--landi-background);
      }
      
      .cart-container {
        max-width: 80rem;
        margin: 0 auto;
        padding: 2rem 1.5rem;
      }
      
      @media (min-width: 640px) {
        .cart-container {
          padding: 3rem;
        }
      }
      
      .cart-header {
        margin-bottom: 2rem;
        padding-bottom: 1.5rem;
        border-bottom: 1px solid var(--landi-border);
      }
      
      .cart-title {
        font-family: var(--landi-font-serif);
        font-size: 2rem;
        font-weight: 500;
        color: var(--landi-text);
        margin: 0;
      }
      
      .cart-count {
        font-family: var(--landi-font-sans);
        font-size: 1rem;
        color: var(--landi-text-muted);
        font-weight: 400;
      }
      
      .cart-layout {
        display: grid;
        grid-template-columns: 1fr;
        gap: 2rem;
      }
      
      @media (min-width: 1024px) {
        .cart-layout {
          grid-template-columns: 1fr 400px;
        }
      }
      
      .cart-items {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
      
      .cart-item {
        display: flex;
        gap: 1.5rem;
        padding: 1.5rem;
        background: var(--landi-surface);
        border-radius: var(--landi-radius-md);
        box-shadow: var(--landi-shadow);
      }
      
      .item-image {
        width: 120px;
        height: 120px;
        object-fit: cover;
        border-radius: var(--landi-radius);
        background: var(--landi-background);
        flex-shrink: 0;
      }
      
      @media (max-width: 640px) {
        .cart-item {
          flex-direction: column;
        }
        
        .item-image {
          width: 100%;
          height: auto;
          aspect-ratio: 4/3;
        }
      }
      
      .item-details {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-width: 0;
      }
      
      .item-name {
        font-family: var(--landi-font-serif);
        font-size: 1.125rem;
        font-weight: 500;
        color: var(--landi-text);
        margin: 0 0 0.25rem;
      }
      
      .item-variant {
        font-size: 0.875rem;
        color: var(--landi-text-muted);
        margin: 0 0 0.5rem;
      }
      
      .item-price {
        font-size: 1rem;
        font-weight: 600;
        color: var(--landi-text);
        margin-bottom: auto;
      }
      
      .item-actions {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin-top: 1rem;
      }
      
      .quantity-controls {
        display: flex;
        align-items: center;
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        overflow: hidden;
      }
      
      .qty-btn {
        width: 36px;
        height: 36px;
        border: none;
        background: var(--landi-surface);
        color: var(--landi-text);
        font-size: 1.125rem;
        cursor: pointer;
        transition: background 200ms ease;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .qty-btn:hover:not(:disabled) {
        background: var(--landi-background);
      }
      
      .qty-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .qty-value {
        width: 50px;
        text-align: center;
        font-size: 0.875rem;
        font-weight: 500;
        background: var(--landi-surface);
        border: none;
        border-left: 1px solid var(--landi-border);
        border-right: 1px solid var(--landi-border);
        color: var(--landi-text);
        padding: 0;
        height: 36px;
      }
      
      .remove-btn {
        padding: 0.5rem;
        background: transparent;
        border: none;
        color: var(--landi-text-light);
        cursor: pointer;
        transition: color 200ms ease;
      }
      
      .remove-btn:hover {
        color: var(--landi-error);
      }
      
      .order-summary {
        position: sticky;
        top: 2rem;
        background: var(--landi-surface);
        border-radius: var(--landi-radius-md);
        padding: 1.5rem;
        box-shadow: var(--landi-shadow);
        height: fit-content;
      }
      
      .summary-title {
        font-family: var(--landi-font-serif);
        font-size: 1.25rem;
        font-weight: 500;
        color: var(--landi-text);
        margin: 0 0 1.5rem;
        padding-bottom: 1rem;
        border-bottom: 1px solid var(--landi-border);
      }
      
      .summary-rows {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        margin-bottom: 1rem;
      }
      
      .summary-row {
        display: flex;
        justify-content: space-between;
        font-size: 0.875rem;
      }
      
      .summary-label {
        color: var(--landi-text-muted);
      }
      
      .summary-value {
        font-weight: 500;
        color: var(--landi-text);
      }
      
      .summary-row.discount .summary-value {
        color: var(--landi-success);
      }
      
      .summary-total {
        display: flex;
        justify-content: space-between;
        padding-top: 1rem;
        border-top: 1px solid var(--landi-border);
        margin-top: 0.5rem;
      }
      
      .total-label {
        font-size: 1rem;
        font-weight: 600;
        color: var(--landi-text);
      }
      
      .total-value {
        font-size: 1.25rem;
        font-weight: 700;
        color: var(--landi-text);
      }
      
      .coupon-section {
        margin: 1.5rem 0;
        padding-top: 1rem;
        border-top: 1px solid var(--landi-border);
      }
      
      .coupon-form {
        display: flex;
        gap: 0.5rem;
      }
      
      .coupon-input {
        flex: 1;
        padding: 0.625rem 0.875rem;
        font-size: 0.813rem;
      }
      
      .coupon-btn {
        padding: 0.625rem 1rem;
        font-size: 0.688rem;
      }
      
      .coupon-error {
        font-size: 0.75rem;
        color: var(--landi-error);
        margin-top: 0.5rem;
      }
      
      .coupon-applied {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.5rem 0.75rem;
        background: var(--landi-success-bg);
        border-radius: var(--landi-radius);
        font-size: 0.813rem;
        color: var(--landi-success);
      }
      
      .checkout-btn {
        width: 100%;
        padding: 1rem;
        font-size: 0.875rem;
        margin-top: 1.5rem;
      }
      
      .continue-shopping {
        display: block;
        text-align: center;
        margin-top: 1rem;
        font-size: 0.813rem;
        color: var(--landi-text-muted);
        cursor: pointer;
        transition: color 200ms ease;
      }
      
      .continue-shopping:hover {
        color: var(--landi-primary);
      }
      
      .trust-badges {
        display: flex;
        justify-content: center;
        gap: 1rem;
        margin-top: 1.5rem;
        padding-top: 1rem;
        border-top: 1px solid var(--landi-border);
      }
      
      .trust-badge {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.75rem;
        color: var(--landi-text-muted);
      }
      
      .empty-cart {
        text-align: center;
        padding: 4rem 2rem;
        background: var(--landi-surface);
        border-radius: var(--landi-radius-md);
        box-shadow: var(--landi-shadow);
      }
      
      .empty-cart-icon {
        width: 80px;
        height: 80px;
        margin: 0 auto 1.5rem;
        color: var(--landi-text-light);
      }
      
      .empty-cart h2 {
        font-family: var(--landi-font-serif);
        font-size: 1.5rem;
        color: var(--landi-text);
        margin: 0 0 0.5rem;
      }
      
      .empty-cart p {
        color: var(--landi-text-muted);
        margin: 0 0 1.5rem;
      }
    `,
  ];

  @property({ type: String, attribute: 'checkout-href' }) checkoutHref = 'checkout.html';
  @property({ type: String, attribute: 'shop-href' }) shopHref = 'index.html#products';

  @state() private cart: Cart = { items: [], subtotal: 0, tax: 0, shipping: 0, discount: 0, total: 0, itemCount: 0 };
  @state() private couponCode = '';
  @state() private couponError = '';

  private unsubscribe?: () => void;

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = cartState.subscribe((cart) => {
      this.cart = cart;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribe?.();
  }

  private updateQuantity(item: CartItem, delta: number) {
    const newQty = item.quantity + delta;
    if (newQty > 0) {
      cartState.updateQuantity(item.id, newQty);
    }
  }

  private removeItem(item: CartItem) {
    cartState.removeItem(item.id);
  }

  private async applyCoupon() {
    if (!this.couponCode.trim()) return;

    const result = await cartState.applyCoupon(this.couponCode.trim());
    if (!result.success) {
      this.couponError = result.message ?? 'Invalid coupon';
      return;
    }
    this.couponError = '';
    this.couponCode = '';
  }

  private handleCheckout() {
    this.dispatchEvent(new CustomEvent('checkout-click', {
      bubbles: true,
      composed: true,
    }));
    window.location.href = this.checkoutHref;
  }

  private handleContinueShopping() {
    this.dispatchEvent(new CustomEvent('continue-shopping', {
      bubbles: true,
      composed: true,
    }));
    window.location.href = this.shopHref;
  }

  render() {
    if (this.cart.items.length === 0) {
      return html`
        <div class="cart-container">
          <div class="empty-cart">
            <svg class="empty-cart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="m1 1 4 4 2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added any items to your cart yet.</p>
            <button class="btn btn-primary btn-lg" @click=${this.handleContinueShopping}>
              Continue Shopping
            </button>
          </div>
        </div>
      `;
    }

    return html`
      <div class="cart-container">
        <header class="cart-header">
          <h1 class="cart-title">
            Shopping Cart 
            <span class="cart-count">(${this.cart.itemCount} ${this.cart.itemCount === 1 ? 'item' : 'items'})</span>
          </h1>
        </header>
        
        <div class="cart-layout">
          <div class="cart-items">
            ${this.cart.items.map(item => {
              const imageDisplay = getProductImageDisplay({
                url: resolveProductImageUrl(item.image),
                name: item.name,
                sku: item.sku,
                theme: 'light',
              });
              return html`
              <article class="cart-item">
                <img 
                  class="item-image" 
                  src=${imageDisplay.src}
                  alt=${item.name}
                  .onerror=${imageDisplay.onError}
                />
                <div class="item-details">
                  <h3 class="item-name">${item.name}</h3>
                  ${item.variantName ? html`
                    <p class="item-variant">${item.variantName}</p>
                  ` : ''}
                  <p class="item-price">${formatPrice(item.price)}</p>
                  
                  <div class="item-actions">
                    <div class="quantity-controls">
                      <button 
                        class="qty-btn"
                        @click=${() => this.updateQuantity(item, -1)}
                        ?disabled=${item.quantity <= 1}
                        aria-label="Decrease quantity"
                      >−</button>
                      <input 
                        class="qty-value" 
                        type="text" 
                        .value=${String(item.quantity)}
                        readonly
                        aria-label="Quantity"
                      />
                      <button 
                        class="qty-btn"
                        @click=${() => this.updateQuantity(item, 1)}
                        aria-label="Increase quantity"
                      >+</button>
                    </div>
                    
                    <button 
                      class="remove-btn"
                      @click=${() => this.removeItem(item)}
                      aria-label="Remove item"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </article>
            `;
            })}
          </div>
          
          <aside class="order-summary">
            <h2 class="summary-title">Order Summary</h2>
            
            <div class="summary-rows">
              <div class="summary-row">
                <span class="summary-label">Subtotal</span>
                <span class="summary-value">${formatPrice(this.cart.subtotal)}</span>
              </div>
              
              <div class="summary-row">
                <span class="summary-label">Shipping</span>
                <span class="summary-value">
                  ${this.cart.shipping > 0 ? formatPrice(this.cart.shipping) : 'Calculated at checkout'}
                </span>
              </div>
              
              <div class="summary-row">
                <span class="summary-label">Tax</span>
                <span class="summary-value">${formatPrice(this.cart.tax)}</span>
              </div>
              
              ${this.cart.discount > 0 ? html`
                <div class="summary-row discount">
                  <span class="summary-label">Discount</span>
                  <span class="summary-value">-${formatPrice(this.cart.discount)}</span>
                </div>
              ` : ''}
            </div>
            
            <div class="summary-total">
              <span class="total-label">Total</span>
              <span class="total-value">${formatPrice(this.cart.total)}</span>
            </div>
            
            <div class="coupon-section">
              ${this.cart.couponCode ? html`
                <div class="coupon-applied">
                  <span>Coupon "${this.cart.couponCode}" applied</span>
                  <button 
                    class="remove-btn"
                    @click=${() => cartState.applyCoupon('')}
                    aria-label="Remove coupon"
                  >×</button>
                </div>
              ` : html`
                <div class="coupon-form">
                  <input 
                    type="text" 
                    class="input coupon-input"
                    placeholder="Enter coupon code"
                    .value=${this.couponCode}
                    @input=${(e: Event) => this.couponCode = (e.target as HTMLInputElement).value}
                  />
                  <button class="btn btn-secondary coupon-btn" @click=${this.applyCoupon}>
                    Apply
                  </button>
                </div>
                ${this.couponError ? html`
                  <p class="coupon-error">${this.couponError}</p>
                ` : ''}
              `}
            </div>
            
            <button class="btn btn-primary checkout-btn" @click=${this.handleCheckout}>
              Proceed to Checkout
            </button>
            
            <button class="continue-shopping" @click=${this.handleContinueShopping}>
              ← Continue Shopping
            </button>
            
            <div class="trust-badges">
              <span class="trust-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                Secure Checkout
              </span>
              <span class="trust-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
                Protected Purchase
              </span>
            </div>
          </aside>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'landi-cart': LandiCart;
  }
}
